(ns common.sync-model
  "What the client is bulk-synced, for an account on the new grid.

   A new-grid-2023 account receives only a minimum generic set of config and
   navigation data in `@session/entities`; every business-record type is fetched
   on demand by whichever view needs it. The decision, the per-type sync scope
   and the UI guidance all live in ai/sync-model.md - read that before changing
   the set here.

   This is cljc because both sides need the same answer. The backend scopes its
   two sync channels by it (`backend.domain.subscription.sync`), and the
   frontend has to know not to *wait* for an entity that will never be pushed to
   it - see the `wait-for-*` helpers in `frontend.state.current-session`.")

(def new-grid-generic-sync-entity-types
  "The minimum generic sync set for a new-grid-2023 account. Scopes both the
  initial candidate pool (`get-tenant-shallow-entities` and its -recent
  siblings) and the live-update channel (`user-tx-data`)."
  #{:entity.type/tenant
    :entity.type/account
    :entity.type/role
    :entity.type/team
    :entity.type/insider-group
    :entity.type/metabase-dashboard
    :entity.type/pipeline
    :entity.type/stage
    :entity.type/check-list
    :entity.type/opportunity-stage
    :entity.type/sales-opportunity-stage
    :entity.type/exit-opportunity-stage
    :entity.type/named-view
    :entity.type/ribbon
    :entity.type/tab
    :entity.type/view
    :entity.type/card
    :entity.type/zap-vault
    :entity.type/vault-config
    :entity.type/fund
    ;; Tenant configuration the settings and navigation UIs read straight out
    ;; of @session/entities, not option lists resolved per picker. Each is
    ;; small (tens per tenant at most), each drives structure rather than
    ;; listing content, and cutting them left the screens below simply empty -
    ;; the "known gap" ai/sync-model.md warned about when these were first
    ;; treated as dropdown taxonomies.
    ;;   investment-reservation-type: settings > decisions > reservations is a
    ;;     CRUD screen over them, and :investment-reservation-type/enabled-fields
    ;;     decides which fields the reservation form renders at all.
    ;;   contact-list: entity-merge computes which lists contain a record.
    ;;   survey-type: one navbar item is built per type, so cutting it removes
    ;;     navigation, not just options. survey-template and survey-block come
    ;;     with it because the surveys settings page reads all three and is
    ;;     half-broken without them.
    ;;   webentry: the web form settings page lists every web form of the
    ;;     tenant and edits them in place. An entity-scope needs a root entity,
    ;;     so it cannot list by type. A tenant has tens of them at most. The
    ;;     navigation footer and the web entry task curtain read the same list.
    ;;   kyc-risk-category: the "Categories" modal on the three KYC detail pages
    ;;     is a CRUD screen for them (rename, renewal interval, delete). A
    ;;     tenant has single digits. The approval label and the calculated
    ;;     blocking status read them too.
    :entity.type/investment-reservation-type
    :entity.type/contact-list
    :entity.type/webentry
    :entity.type/kyc-risk-category
    :entity.type/survey-type
    :entity.type/survey-template
    :entity.type/survey-block
    ;; Not a business record: a job is this account's own transient progress
    ;; row for an import, export or report, and the UI reads its :job/status and
    ;; :job/result straight out of @session/entities to know the work finished.
    ;; `job-owner?` in the backend's sync already narrows it to the requesting
    ;; account's own jobs from the last hour, so keeping the type here syncs
    ;; nobody else's. Cutting it stalled data import, custom reports, the
    ;; dashboard and settings->export indefinitely, because the status they
    ;; wait for stopped arriving.
    :entity.type/job})

(def entity-scope-skipped-entity-types
  "Entity types that an entity-scope subscription does not send. The client has
  them from the generic sync, so the reply and the live deltas of a scope would
  only repeat them. They are the tenant, the accounts, the teams, the pipelines
  and the stages of every pipeline type. Together they were 94 to 99 percent of
  the bytes of a scope reply in the QA tenant, mainly the tenant entity and the
  members of the teams.

  The generic sync sends only the staff accounts. A guest account is not in the
  generic sync, so the scope still sends it. See `skipped-by-entity-scope?` in
  `backend.domain.updates.topics.context.entity-scope`, which applies this set.

  The scope does not walk through a skipped entity either. The entities that
  only a skipped entity points at are not part of the scope. The root entity of a
  scope is always sent, also when its type is in this set.

  A role is not in the set. No business entity points at a role."
  #{:entity.type/tenant
    :entity.type/account
    :entity.type/team
    :entity.type/pipeline
    :entity.type/stage
    :entity.type/opportunity-stage
    :entity.type/sales-opportunity-stage
    :entity.type/exit-opportunity-stage})

(defn bulk-synced-entity-type?
  "Will `entity-type` reach `@session/entities` through the bulk sync channels?

  True for everything on an old-grid account. On a new-grid account only the
  generic set arrives, so anything else must be fetched on demand - and must
  never be waited for, since nothing will ever deliver it."
  [{:keys [new-grid? entity-type]}]
  (or (not new-grid?)
      (contains? new-grid-generic-sync-entity-types entity-type)))
